import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSquare,
  OptionItemSquareList,
} from '@/pages/Onboarding/components/OptionItemSquare';
import { useOnboarding } from '@/pages/Onboarding/context';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle } from '../../../OwnAI/components/Title';
import { LayoutDataType } from '../../types';

import { ScreenStyles } from './BodyShape.styles';
import {
  BODY_SHAPE_OPTIONS_FEMALE,
  BODY_SHAPE_OPTIONS_MALE,
} from './constants';

const ASSETS_PATH = `${S3_BUCKET_URL}/layout-own-ai-multigender-forest/body-shape`;

export const ScreenBodyShape: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'BodyShape',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const bodyShape = form.watch('body_shape');
  const gender = form.watch('gender');

  const options =
    gender === 'male' ? BODY_SHAPE_OPTIONS_MALE : BODY_SHAPE_OPTIONS_FEMALE;

  const handleSelect = (selectedBodyShape: string) => {
    form.setValue('body_shape', selectedBodyShape);
    userService.createOrUpdate({
      onboarding_data: {
        body_shape: selectedBodyShape,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const getImageSrc = (bodyShapeId: string) => {
    const genderFolder = gender === 'male' ? 'male' : 'female';
    return `${ASSETS_PATH}/${genderFolder}/${bodyShapeId}.png`;
  };

  const titlePronoun = gender === 'male' ? 'his' : 'her';

  return (
    <ScreenStyles background="dark-solid">
      <OnboardingNavigation />

      <OwnAiTitle>Choose {titlePronoun} body shape</OwnAiTitle>

      <IonContent>
        <OptionItemSquareList>
          {options.map((option) => (
            <OptionItemSquare
              key={option.id}
              isSelected={bodyShape === option.id}
              onClick={() => handleSelect(option.id)}
              bgSrc={getImageSrc(option.id)}
              title={option.title}
              TEST_ID={`body_shape-${option.id}`}
            />
          ))}
        </OptionItemSquareList>
      </IonContent>
    </ScreenStyles>
  );
};
